import type { OdooTask } from '@/types/odoo';
import type { EasykitOverlayTask } from '@/api/easykit';
import { formatDateKey } from '@/utils/planning';

// Re-export shared planning utils for convenience
export { getWeekDates, formatWeekLabel, getDayLabel, isToday, formatDateKey } from '@/utils/planning';

/** Nederlands — gebruikt door detail-panelen en kaarten. */
const DUTCH_DAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'] as const;
const DUTCH_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'] as const;

/** Format a YYYY-MM-DD date as `wo 27 mei 2026`. Empty string for invalid input. */
export function formatDutchDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return `${DUTCH_DAYS[d.getDay()]} ${d.getDate()} ${DUTCH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** EasyKit task tags — single source of truth. */
export const EASYKIT_TAGS = [
  { id: 7, label: 'Voormiddag', toggled: 'bg-green-100 text-green-800 border-green-300', card: 'bg-green-200 text-green-800', untoggled: 'bg-white text-gray-500 border-gray-200' },
  { id: 6, label: 'Namiddag',  toggled: 'bg-blue-100 text-blue-800 border-blue-300',   card: 'bg-blue-200 text-blue-800',  untoggled: 'bg-white text-gray-500 border-gray-200' },
  { id: 12, label: 'Afhaling', toggled: 'bg-amber-100 text-amber-800 border-amber-300', card: 'bg-amber-200 text-amber-800', untoggled: 'bg-white text-gray-500 border-gray-200' },
] as const;

/**
 * Does this task belong in the EK grid cell {driverId, dateKey}?
 *   - driverId 0 = Afhalingen → tasks with no installateur.
 *   - driverId > 0 → tasks whose installateur array includes that id.
 * Used both for live grouping and for post-move recomputation in the store.
 */
export function taskBelongsToCell(task: OdooTask, driverId: number, dateKey: string): boolean {
  if (typeof task.date_deadline !== 'string') return false;
  if (task.date_deadline.slice(0, 10) !== dateKey) return false;
  const inst = task.x_studio_installateur ?? [];
  if (driverId === 0) return inst.length === 0;
  return inst.includes(driverId);
}

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

/**
 * Convert an Odoo HTML description field to readable plain text:
 * block tags → newlines, all other tags stripped, common HTML entities decoded.
 * Entity decoding runs AFTER tag stripping, so a decoded `<`/`>` can never
 * re-introduce a tag (React escapes the result anyway). Deterministic — no DOM dependency.
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    // Decode entities AFTER tag-stripping (decoded </> can't re-introduce a tag),
    // and BEFORE trim so a trailing &nbsp; doesn't leave a dangling space.
    .replace(/&(?:lt|gt|quot|apos|nbsp|#39);/g, (m) => HTML_ENTITIES[m] ?? m)
    .replace(/&amp;/g, '&')
    .trim();
}

/**
 * Mirror of the server's task-description write transform (routes/projects.ts
 * PATCH /tasks/:taskId/description): HTML-escape each line, join with <br>.
 * Used to sync the cached `task.description` after a successful save, so
 * `stripHtml(descriptionToHtml(text)) === text.trim()` and the description
 * editor never resets to pre-save text after a task/fase switch.
 */
export function descriptionToHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[ch]!));
  return text.split('\n').map(escape).join('<br>');
}

export type EasykitTaskType = 'druktest' | 'levering' | 'feestdag' | 'other';

/** Authoritative EasyKit project IDs (Odoo project.project). */
export const EASYKIT_PROJECT_HOLIDAYS = 955;  // "Feestdagen/Verlof"
export const EASYKIT_PROJECT_LOOSE = 956;     // "EasyKit losse taken"

/** Authoritative EasyKit task stage IDs (stable across all EK projects). */
export const EASYKIT_STAGE_LEVERING = 237;  // "Basisplan"
export const EASYKIT_STAGE_DRUKTEST = 360;  // "Drukattest"

/** Color pill labels rendered next to the task headline on every EK surface
 *  (planning card, detail panel, driver mobile + desktop, mirror view, driver
 *  task detail). Single source of truth for these labels + classes. */
export const TYPE_PILL: Record<EasykitTaskType, { label: string; cls: string } | null> = {
  druktest: { label: 'DT', cls: 'bg-red-600 text-white' },
  levering: { label: 'LEV', cls: 'bg-blue-600 text-white' },
  feestdag: { label: 'FD', cls: 'bg-purple-600 text-white' },
  other: null,
};

/** Determine task type. Drukattest stage (360) is authoritative \u2014 Hendrika
 *  never moves tasks out of it. Basisplan (237) is the typical levering stage
 *  but tasks CAN be moved out, so we fall back to soSummary (materialen lines
 *  exist) or name marker. Losse taken (stage "Nieuw") rely on name only. */
export function getTaskType(
  name: string,
  stageId: number | null,
  projectId?: number,
  hasSoSummary = false,
): EasykitTaskType {
  if (projectId === EASYKIT_PROJECT_HOLIDAYS) return 'feestdag';
  if (stageId === EASYKIT_STAGE_DRUKTEST) return 'druktest';
  if (stageId === EASYKIT_STAGE_LEVERING) return 'levering';
  const lower = name.toLowerCase();
  if (name.includes('DT') || lower.includes('druktest')) return 'druktest';
  if (hasSoSummary || name.includes('m\u00b2') || lower.includes('m2')) return 'levering';
  return 'other';
}

/** Marker task names used for day status.
 *  Mirrored on the backend at portal-api/src/routes/easykit.ts (MARKER_NAMES).
 *  Keep in sync — these are cross-runtime constants without a shared package. */
const MARKER_NAMES = new Set(['OPEN', 'LEV', '.']);

/** Returns true if the task name is a day status marker */
export function isMarkerTask(name: string): boolean {
  return MARKER_NAMES.has(name.trim());
}

/**
 * Canonical within-cell ordering for EasyKit day cells: explicit `sequence`
 * first, then `id` as a stable tiebreak. BOTH the planning grid (see
 * groupByDriverAndDay) and the driver-mirror print use this so a driver's
 * printed day always matches the order the projectleider arranged. The id
 * tiebreak matters because most tasks share a sequence (Odoo default 10, or 0
 * for hand-created tasks) — without it the mirror fell back to Odoo's physical
 * row order and diverged from the grid.
 */
export function compareCellOrder(
  a: { sequence?: number | null; id: number },
  b: { sequence?: number | null; id: number },
): number {
  return (a.sequence ?? 0) - (b.sequence ?? 0) || a.id - b.id;
}

/**
 * Group tasks by driver and day.
 * Returns Map<driverId, Map<dateKey, OdooTask[]>>
 * driverId 0 is used for "Afhalingen" — tasks with no installer or no matching driver.
 */
export function groupByDriverAndDay(
  tasks: OdooTask[],
  days: Date[],
  driverIds: number[],
  includeAfhalingen = true,
): Map<number, Map<string, OdooTask[]>> {
  const validDateKeys = new Set(days.map(formatDateKey));
  const driverIdSet = new Set(driverIds);
  const result = new Map<number, Map<string, OdooTask[]>>();

  // Initialize all driver/day combinations
  for (const driverId of driverIds) {
    const dayMap = new Map<string, OdooTask[]>();
    for (const day of days) {
      dayMap.set(formatDateKey(day), []);
    }
    result.set(driverId, dayMap);
  }

  // Initialize Afhalingen bucket (driverId 0)
  if (includeAfhalingen) {
    const dayMap = new Map<string, OdooTask[]>();
    for (const day of days) {
      dayMap.set(formatDateKey(day), []);
    }
    result.set(0, dayMap);
  }

  // Place tasks into their driver/day buckets
  for (const task of tasks) {
    if (!task.date_deadline || typeof task.date_deadline !== 'string') continue;
    const dateKey = task.date_deadline.slice(0, 10);
    if (!validDateKeys.has(dateKey)) continue;

    // Skip marker tasks
    if (isMarkerTask(task.name)) continue;

    const installerIds = task.x_studio_installateur ?? [];
    let placed = false;

    for (const instId of installerIds) {
      if (driverIdSet.has(instId)) {
        const dayMap = result.get(instId);
        if (dayMap) {
          const dayTasks = dayMap.get(dateKey);
          if (dayTasks) {
            dayTasks.push(task);
            placed = true;
          }
        }
      }
    }

    // Orphan tasks go into Afhalingen bucket (driverId 0)
    if (!placed && includeAfhalingen) {
      const dayMap = result.get(0);
      if (dayMap) {
        const dayTasks = dayMap.get(dateKey);
        if (dayTasks) {
          dayTasks.push(task);
        }
      }
    }
  }

  // Sort tasks by sequence (from Odoo) within each day cell
  for (const dayMap of result.values()) {
    for (const [key, dayTasks] of dayMap) {
      dayMap.set(key, dayTasks.sort(compareCellOrder));
    }
  }

  return result;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Trello-style multiple-containers drag layout (EK planning grid).
 * All pure + immutable — no React, no fetch — so they're fully unit-testable.
 *
 * TWO distinct ID formats with different field orders (hence the explicit
 * helper-pair naming — DO NOT mix them):
 *   - DOM / dnd-kit ids:
 *       task sortable id  = `task:${taskId}`              (taskDndId)
 *       cell droppable id = `cell:${dateKey}:${driverId}` (dndCellId, dateKey FIRST)
 *   - Internal drag-layout map key:
 *       `${driverId}:${dateKey}`                          (layoutCellKey, driverId FIRST)
 *
 * Helper pairs:
 *   - dndCellId / parseDndCellId       — DOM-side
 *   - layoutCellKey / parseLayoutCellKey — internal Map keys
 * ─────────────────────────────────────────────────────────────────────────── */

export type EkLayout = Map<string, number[]>;

export function layoutCellKey(driverId: number, dateKey: string): string {
  return `${driverId}:${dateKey}`;
}

export function parseLayoutCellKey(key: string): { driverId: number; dateKey: string } {
  const idx = key.indexOf(':');
  return { driverId: Number(key.slice(0, idx)), dateKey: key.slice(idx + 1) };
}

export function dndCellId(driverId: number, dateKey: string): string {
  // DOM/dnd-kit id format: dateKey FIRST, driverId LAST (matches existing useDroppable).
  return `cell:${dateKey}:${driverId}`;
}

export function parseDndCellId(id: string): { driverId: number; dateKey: string } | null {
  if (!id.startsWith('cell:')) return null;
  const rest = id.slice(5);
  const lastColon = rest.lastIndexOf(':');
  if (lastColon === -1) return null;
  const dateKey = rest.slice(0, lastColon);
  const driverId = Number(rest.slice(lastColon + 1));
  if (!Number.isFinite(driverId) || !dateKey) return null;
  return { driverId, dateKey };
}

export function taskDndId(taskId: number): string {
  return `task:${taskId}`;
}

/** Parse a dnd-kit active id (`task:123` or bare `123`) to a numeric task id, or null. */
export function parseTaskId(id: string | number): number | null {
  const s = String(id);
  const n = s.startsWith('task:') ? Number(s.slice(5)) : Number(s);
  return Number.isFinite(n) ? n : null;
}

export type EkOverTarget =
  | { kind: 'cell'; driverId: number; dateKey: string }
  | { kind: 'task'; taskId: number }
  | null;

/** Normalise a dnd-kit `over.id` into a cell or task target. */
export function parseOverTarget(id: string | number): EkOverTarget {
  const s = String(id);
  const cell = parseDndCellId(s);
  if (cell) return { kind: 'cell', ...cell };
  const taskId = parseTaskId(s);
  return taskId === null ? null : { kind: 'task', taskId };
}

/** Build the initial drag layout: every visible cell (incl. empty + Afhalingen 0) → ordered task ids. */
export function buildDragLayout(
  grouped: Map<number, Map<string, OdooTask[]>>,
  driverIds: number[],
  dateKeys: string[],
): EkLayout {
  const layout: EkLayout = new Map();
  const containers = [...driverIds, 0]; // 0 = Afhalingen
  for (const driverId of containers) {
    for (const dateKey of dateKeys) {
      const tasks = grouped.get(driverId)?.get(dateKey) ?? [];
      layout.set(layoutCellKey(driverId, dateKey), tasks.map((t) => t.id));
    }
  }
  return layout;
}

export function findLayoutContainer(layout: EkLayout, taskId: number): string | null {
  for (const [key, ids] of layout) {
    if (ids.includes(taskId)) return key;
  }
  return null;
}

/**
 * Move `activeId` within/between containers, returning a NEW layout.
 * - over a cell → append to that container (end).
 * - over a task → insert before it (insertAfter=false) or after it (insertAfter=true).
 * No-op-safe: if the computed position equals the current one, returns an equivalent layout.
 */
export function moveInLayout(
  layout: EkLayout,
  activeId: number,
  over: EkOverTarget,
  opts: { insertAfter: boolean },
): EkLayout {
  if (!over) return layout;
  const fromKey = findLayoutContainer(layout, activeId);
  if (fromKey === null) return layout;

  let toKey: string;
  let toIndex: number;
  if (over.kind === 'cell') {
    toKey = layoutCellKey(over.driverId, over.dateKey);
    toIndex = (layout.get(toKey)?.length ?? 0);
  } else {
    if (over.taskId === activeId) return layout;
    const overKey = findLayoutContainer(layout, over.taskId);
    if (overKey === null) return layout;
    toKey = overKey;
    const overArr = layout.get(toKey)!;
    toIndex = overArr.indexOf(over.taskId) + (opts.insertAfter ? 1 : 0);
  }

  const next: EkLayout = new Map();
  for (const [k, ids] of layout) next.set(k, ids.filter((id) => id !== activeId));
  const target = [...next.get(toKey)!];
  // Clamp; when removing from the same container the index may shift but splice handles it.
  target.splice(Math.min(toIndex, target.length), 0, activeId);
  next.set(toKey, target);
  return next;
}

export type EkCommit =
  | { kind: 'noop' }
  | { kind: 'reorder'; driverId: number; dateKey: string; orderedIds: number[] }
  | { kind: 'crossCell'; taskId: number; toDriverId: number; toDate: string; orderedIds: number[]; insertBeforeTaskId: number | null };

/** Diff the active card's position between the original and final layouts. */
export function deriveLayoutCommit(original: EkLayout, final: EkLayout, activeId: number): EkCommit {
  const fromKey = findLayoutContainer(original, activeId);
  const toKey = findLayoutContainer(final, activeId);
  if (fromKey === null || toKey === null) return { kind: 'noop' };

  const { driverId: toDriverId, dateKey: toDate } = parseLayoutCellKey(toKey);
  const finalArr = final.get(toKey)!;
  const finalIndex = finalArr.indexOf(activeId);
  const insertBeforeTaskId = finalArr[finalIndex + 1] ?? null;

  if (fromKey === toKey) {
    const origArr = original.get(fromKey)!;
    const sameOrder = origArr.length === finalArr.length && origArr.every((id, i) => id === finalArr[i]);
    if (sameOrder) return { kind: 'noop' };
    const { driverId, dateKey } = parseLayoutCellKey(fromKey);
    return { kind: 'reorder', driverId, dateKey, orderedIds: finalArr };
  }

  return { kind: 'crossCell', taskId: activeId, toDriverId, toDate, orderedIds: finalArr, insertBeforeTaskId };
}

/**
 * Group overlay (non-EK) tasks by driver and day. Unlike groupByDriverAndDay:
 *   - no marker-name skip (overlay tasks are never volzet markers)
 *   - no Afhalingen bucket (overlay shows per real driver only)
 *   - a multi-installer task lands in EVERY matching driver's bucket
 */
export function groupOverlayByDriverAndDay(
  overlayTasks: EasykitOverlayTask[],
  days: Date[],
  driverIds: number[],
): Map<number, Map<string, EasykitOverlayTask[]>> {
  const validDateKeys = new Set(days.map(formatDateKey));
  const driverIdSet = new Set(driverIds);
  const result = new Map<number, Map<string, EasykitOverlayTask[]>>();

  for (const driverId of driverIds) {
    const dayMap = new Map<string, EasykitOverlayTask[]>();
    for (const day of days) dayMap.set(formatDateKey(day), []);
    result.set(driverId, dayMap);
  }

  for (const task of overlayTasks) {
    if (!task.date_deadline) continue;
    const dateKey = task.date_deadline.slice(0, 10);
    if (!validDateKeys.has(dateKey)) continue;
    for (const instId of task.installerIds) {
      if (!driverIdSet.has(instId)) continue;
      result.get(instId)?.get(dateKey)?.push(task);
    }
  }

  return result;
}
